/**
 * Helpers PUROS de la grabación de audio en el navegador. Sin DOM, sin React: todo lo testeable
 * del `MediaRecorder` vive acá y el hook (`hooks/useAudioRecorder`) se queda sólo con el ciclo de
 * vida.
 *
 * Nació en el widget de feedback (`feedback_widget/feedbackAudio.js`, que hoy es un re-export de
 * esto) y se compartió cuando la Memoria del cliente sumó la nota de audio. Los defaults
 * (`MAX_AUDIO_SECONDS`, `MAX_AUDIO_BYTES`, el prefijo de las keys de error) son los del widget:
 * quien necesite otros los pasa por parámetro.
 */

export const MAX_AUDIO_SECONDS = 120;
export const NEAR_LIMIT_SECONDS = 10;
// Red de seguridad: 2 min de opus son ~360 KB, pero el AAC de Safari pesa bastante más.
export const MAX_AUDIO_BYTES = 8 * 1024 * 1024;
export const DEFAULT_ERROR_KEY_PREFIX = 'feedback.audio';

/**
 * Candidatos en orden de preferencia. El orden NO es estético:
 * - webm/opus es lo que dan Chrome y Edge, y es el más liviano.
 * - ogg/opus es Firefox.
 * - **audio/mp4 es Safari**, que NO soporta webm/opus. Sin esta entrada, `MediaRecorder` en Safari
 *   tira al construirse o graba un blob vacío — el usuario ve el contador correr y no se guarda
 *   nada.
 * - El último es "que elija el browser": ahí se lee `recorder.mimeType` real al parar.
 */
const MIME_CANDIDATES = [
    {mimeType: 'audio/webm;codecs=opus', extension: 'webm'},
    {mimeType: 'audio/webm', extension: 'webm'},
    {mimeType: 'audio/ogg;codecs=opus', extension: 'ogg'},
    {mimeType: 'audio/mp4', extension: 'm4a'},
];

export function isAudioRecordingSupported() {
    return (typeof window !== 'undefined'
        && typeof window.MediaRecorder !== 'undefined'
        && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia));
}

/**
 * El primer mimeType que el navegador soporte, con su extensión.
 *
 * `isTypeSupported` puede no existir en implementaciones viejas: sin el guard, el widget explota
 * en un navegador donde igual podríamos grabar dejando que el browser elija.
 */
export function pickAudioMimeType() {
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
        return {mimeType: '', extension: 'webm'};
    }
    if (typeof window.MediaRecorder.isTypeSupported !== 'function') {
        return {mimeType: '', extension: 'webm'};
    }
    const found = MIME_CANDIDATES.find((c) => window.MediaRecorder.isTypeSupported(c.mimeType));
    return found || {mimeType: '', extension: 'webm'};
}

/** Extensión a partir del mimeType REAL que reportó el recorder (puede diferir del pedido). */
export function extensionForMimeType(mimeType) {
    if (!mimeType) return 'webm';
    const normalized = String(mimeType).toLowerCase();
    if (normalized.includes('ogg')) return 'ogg';
    if (normalized.includes('mp4') || normalized.includes('m4a')) return 'm4a';
    if (normalized.includes('mpeg')) return 'mp3';
    if (normalized.includes('wav')) return 'wav';
    return 'webm';
}

/** `125000` → `"02:05"`. */
export function formatElapsed(ms) {
    const totalSeconds = Math.max(0, Math.floor((ms || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** `125000` → `"2:05"` — la forma corta del mockup de la Barra Total («Nota de audio — 1:23»). */
export function formatElapsedShort(ms) {
    const totalSeconds = Math.max(0, Math.floor((ms || 0) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Traduce el error de `getUserMedia` a una key de i18n bajo `prefix` (`<prefix>.error_denied`…).
 *
 * El mockup asume que el permiso siempre se concede. En la vida real el caso más común es
 * `NotAllowedError`, y sin un mensaje que diga qué hacer el usuario cree que el botón está roto.
 */
export function describeMicError(error, prefix = DEFAULT_ERROR_KEY_PREFIX) {
    const name = error && error.name ? error.name : '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
        return `${prefix}.error_denied`;
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return `${prefix}.error_not_found`;
    }
    if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
        return `${prefix}.error_busy`;
    }
    return `${prefix}.error_generic`;
}

// --- medición de nivel en vivo (Web Audio) --- //
//
// El contador y el punto rojo se mueven igual con el micrófono mudo: no prueban NADA. Lo único que
// prueba que te estamos escuchando es que las barras se muevan con tu voz. Todo lo que se puede
// calcular sin DOM vive acá; el `AnalyserNode` lo maneja el hook.

/**
 * Pico por debajo del cual consideramos que en la grabación no hubo sonido.
 *
 * Calibrado sobre el RMS normalizado (0..1): el ruido de sala de un micrófono abierto queda en
 * ~0.005-0.015 y una voz normal pasa 0.05 largo. El umbral NO bloquea nada — sólo enciende un
 * aviso, porque un micrófono con ganancia muy baja puede grabar voz real por debajo de esto.
 */
export const SILENCE_PEAK_LEVEL = 0.02;

/** Cuántas barras dibuja el medidor. Impar: la del medio queda centrada bajo el cursor visual. */
export const METER_BANDS = 5;

/**
 * RMS de un `getByteTimeDomainData` (Uint8Array centrada en 128) → 0..1.
 *
 * Se usa el RMS y no el pico muestra a muestra porque el pico salta con cualquier chasquido y haría
 * parpadear el medidor entero con un ruido de teclado.
 */
export function levelFromTimeDomain(samples) {
    if (!samples || !samples.length) return 0;
    let sum = 0;
    for (let i = 0; i < samples.length; i += 1) {
        const value = (samples[i] - 128) / 128;
        sum += value * value;
    }
    return Math.sqrt(sum / samples.length);
}

/**
 * Reparte un `getByteFrequencyData` en `bandCount` bandas promediadas → array de 0..1.
 *
 * Las bandas son el espectro REAL, no un adorno con random: por eso el medidor se mueve distinto
 * con una voz grave que con una aguda, y por eso se queda quieto de verdad cuando no hay nada.
 * Se descarta el último tercio del espectro (arriba de ~8 kHz no hay voz y sólo aporta ruido plano).
 */
export function bandsFromFrequencies(frequencies, bandCount = METER_BANDS) {
    const bands = new Array(bandCount).fill(0);
    if (!frequencies || !frequencies.length) return bands;
    const usable = Math.max(bandCount, Math.floor(frequencies.length * 0.66));
    const size = Math.floor(usable / bandCount) || 1;
    for (let band = 0; band < bandCount; band += 1) {
        const from = band * size;
        const to = Math.min(from + size, usable);
        let sum = 0;
        for (let i = from; i < to; i += 1) sum += frequencies[i];
        bands[band] = to > from ? (sum / (to - from)) / 255 : 0;
    }
    return bands;
}

/**
 * Nivel 0..1 → altura 0..1 de una barra.
 *
 * La raíz cuadrada con ganancia es lo que hace que una voz normal llene el medidor: en lineal, un
 * RMS de 0.1 dibuja barras de un 10% de alto y la pantalla parece rota justo cuando funciona.
 */
export function meterHeight(level, gain = 2.2) {
    const value = Math.sqrt(Math.max(0, level) * gain);
    return Math.max(0, Math.min(1, value));
}
